import { Injectable, NgZone } from '@angular/core';
import { ToastrService, IndividualConfig, ToastRef, ActiveToast } from 'ngx-toastr';
import { Subject,  Observable, BehaviorSubject, distinctUntilChanged, interval } from 'rxjs';
import { AppMessage } from './app-message';
import { environment } from '../../../environments/environment';
import { StateService } from '../state/state.service';
import { QmReplacePipe } from '../../components/qm-pipes/qm-replace.pipe';
import { TranslateService } from '@ngx-translate/core';
import { Reminder } from '../../reminder/reminder.model';
import { _ } from '../../shared/utils';
import { format } from 'date-fns';
import fi from 'date-fns/locale/fi';
import { PushNotificationsService } from './push-notifications.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { NotificationEntry } from '~app/components/notification/notification.model';
import { VivoNotification } from '~app/components/notification/new-notification/vivo-notification/vivo-notification.model';
import { Comment } from '~app/components/notification/comment/comment.model';

@Injectable()
export class AppMessageService {

  /**
   * alarm listener Subject
   * @type {Subject<any>}
   */
  private alarmData = new Subject<AppMessage>();
  /**
   * Observable for alarm listener Subject
   * @type {Observable<any>}
   */
  public alarmChanged$: Observable<AppMessage> = this.alarmData.asObservable();

   /**
   * References to currently displayed messages by reminder id.
   */
  public reminders = new Map<number, {toastRef: ToastRef<any>, notificationRef: Notification}>();

  private currentlyActive = new BehaviorSubject<number>(0);
  public currentlyActive$: Observable<number> = this.currentlyActive.asObservable().pipe(distinctUntilChanged());

  constructor(
    zone: NgZone,
    private toastr: ToastrService,
    private stateService: StateService,
    private push: PushNotificationsService,
    private translate: TranslateService,
    private sidebarService: SidebarService) {
    // Just in case the state gets out of sync somehow...
    // The zone stuff is needed for Angular to reach a "stable" state, allowing app startup
    zone.runOutsideAngular(() => {
      setInterval(() => {
        zone.run(() => {
          this.refreshCurrentlyActive();
        });
      }, 10_000);
    });
  }

  /**
   * Shows a push notification on the desktop if the browser isn't visible.
   * Always shows a toast notification inside the app.
   * Syncs dismissing both kinds of notifications.
   */
  private pushAndToast({title, body, variant, skipPush, toastConfig, onClick, onShow, onHide}: {
    title: string,
    body: string,
    variant: string,
    skipPush?: boolean,
    toastConfig?: Partial<IndividualConfig>,
    onClick?: () => void,
    onShow?: (notification: Notification | null, toast: ActiveToast<any>) => void,
    onHide?: () => void,
  }) {
    const showToast = (notification: Notification | null): ActiveToast<any> => {
      // defaults come from AppModule
      const toast = this.toastr.show(body, title, toastConfig ?? {}, `toast-${variant}`);

      onShow?.(notification, toast);

      toast.onTap.subscribe(() => {
        onClick?.();
      });
      toast.onHidden.subscribe(() => {
        onHide?.();
        notification?.close();
        this.refreshCurrentlyActive();
      });

      this.refreshCurrentlyActive();
      return toast;
    }

    if (skipPush || document.visibilityState === 'visible' || !this.push.hasPermission()) {
      showToast(null);
      return;
    }

    let toast: ActiveToast<any>;
    this.push.create(`REGO ${title}`, {
      body,
      icon: environment.assetRoot + '/favicon/192.png',
      requireInteraction: toastConfig?.disableTimeOut === true,
    }).subscribe({
      next: ({ notification, event }) => {
        switch (event.type) {
          case 'show':
            toast = showToast(notification);
            break;
          case 'click':
            onHide = null; // prevent toast from calling `onHide` when it's closed
            toast?.toastRef.close();
            onClick?.();
            break;
        }
      },
      // If notification was rejected (no permissions for instance)
      error: (err) => {
        console.log(err);
        showToast(null);
      },
      complete: () => {
        toast?.toastRef.close();
      }
    });
  }

  /**
   * Add new status listener
   * @param alarmMessage
   */
  public broadcastAlarm(alarmMessage: AppMessage) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      alarmMessage.message = this.getTranslation(alarmMessage.message);

      /// NOTE: info level messages are not translated ?
      // defaults come from AppModule
      const toastConfigurationForAlarm: Partial<IndividualConfig> = {};
      const autocloseAlarms = this.stateService.getPartOfState('settingsState').autocloseAlarms ?? true;
      if (!autocloseAlarms) {
        toastConfigurationForAlarm.disableTimeOut = true;
      }

      if (alarmMessage.toast) {
        let toast: ActiveToast<unknown>;
        switch (alarmMessage.priority) {
          case 'success':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            toast = this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-success');
            break;
          case 'warning':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            toast = this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-warning');
            break;
          case 'error':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            toast = this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-custom-error');
            break;
          default:
            toast = this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-info');
            break;
        }

        toast.onTap.subscribe(() => this.sidebarService.requestSidebarPanel(alarmMessage.component));
        toast.onHidden.subscribe(() => this.refreshCurrentlyActive());
        this.refreshCurrentlyActive();
      }
    }
    this.alarmData.next(alarmMessage);
  }

  public broadcastNotification(notification: NotificationEntry) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Message')),
        body: notification.description,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushNotifications,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Notification');
        }
      });
    }
  }

  public broadcastNotificationComment(comment: Comment) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Response')),
        body: comment.comment,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushNotifications,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Notification');
        }
      });
    }
  }

  public broadcastDiary(notification: VivoNotification) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Diary')),
        body: notification.message,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushDiary,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Diary');
        }
      });
    }
  }

  public broadcastReminder(reminder: Reminder) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      let title = this.getTranslation(_('Reminder'));
      if (reminder.backendReminder.alarmingTime) {
        title += ' ';
        title += this.formatDate(reminder.backendReminder.alarmingTime);
      }

      if (this.reminders.has(reminder.backendReminder.id)) {
        return;
      } else {
        this.reminders.set(reminder.backendReminder.id, {notificationRef: null, toastRef: null});
      }

      this.pushAndToast({
        title,
        body: reminder.backendReminder.description,
        variant: 'reminder',
        toastConfig: { disableTimeOut: true },
        onShow: (notification, toast) => {
          this.reminders.set(reminder.backendReminder.id, {notificationRef: notification, toastRef: toast.toastRef});
        },
        onHide: () => {
          reminder.cancelAlarm();
        }
      });
    }
  }

  public hideReminder(reminder: Reminder) {
    if (this.reminders.has(reminder.backendReminder.id)) {
      const {notificationRef, toastRef} = this.reminders.get(reminder.backendReminder.id);
      this.reminders.delete(reminder.backendReminder.id);
      if (toastRef) {
        toastRef.close();
      }
      if (notificationRef) {
        notificationRef.close();
      }
    }
  }

  private getTranslation(msg: string): string {
    try {
      return this.translate.instant(msg);
    } catch (e) {
      return msg;
    }
  }

  private formatDate(value: Date): string {
    const form = 'HH:mm';
    if (this.stateService.getPartOfState('settingsState').language === 'fi') {
      return format(value, form, {locale: fi});
    } else {
      return format(value, form);
    }
  }


  private refreshCurrentlyActive(): void {
    // `toastr` seems to dispatch events _before_ modifying its public state
    requestAnimationFrame(() => {
      // duplicates are handled by `this.currentlyActive$`
      this.currentlyActive.next(this.toastr.currentlyActive);
    });
  }

  public closeAll(): void {
    this.toastr.clear();
  }
}
